import { useMemo, useState } from "react";
import { ArrowUpRight, Zap, PhoneCall, Star } from "lucide-react";
import { servicesContent } from "@/data/servicesContent";

const catalogTabs = [
  { id: "all", label: "All Services" },
  { id: "growth", label: "Marketing" },
  { id: "web", label: "Web / App" },
  { id: "video", label: "AI Video" },
  { id: "ai", label: "Private AI" },
  { id: "launch", label: "Zero to Launch" },
  { id: "saas", label: "SaaS" },
  { id: "os", label: "TraceElements OS" },
  { id: "geo", label: "GEO Dominance" },
];

export default function PortalServiceCatalog({ onStartInstantly, onBookCall, search }) {
  const [tab, setTab] = useState("all");

  const filtered = useMemo(() => {
    const term = (search || "").trim().toLowerCase();
    return Object.entries(servicesContent).filter(([slug, svc]) => {
      if (tab !== "all" && svc.category !== tab) return false;
      if (!term) return true;
      return (
        slug.includes(term) ||
        svc.name.toLowerCase().includes(term) ||
        svc.tagline?.toLowerCase().includes(term) ||
        svc.categoryLabel?.toLowerCase().includes(term)
      );
    });
  }, [tab, search]);

  return (
    <section
      className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 border-t border-[#1F2833]"
      data-testid="portal-service-catalog"
    >
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-2">
            // SERVICE.CATALOG
          </div>
          <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">
            Self-serve or book a call. <span className="text-emerald-400">Your call.</span>
          </h2>
        </div>
        <div className="font-mono text-[11px] text-[#C5C6C7]/70">
          {filtered.length} package{filtered.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-6" role="tablist">
        {catalogTabs.map((t) => {
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`catalog-tab-${t.id}`}
              className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.22em] border transition-all ${
                isActive
                  ? "border-[#FF3355] bg-[#FF3355]/10 text-[#FF3355]"
                  : "border-[#1F2833] text-[#C5C6C7] hover:border-[#B91C2C] hover:text-[#F5F5F7]"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Cards */}
      {filtered.length === 0 ? (
        <div className="border border-[#1F2833] p-10 text-center font-mono text-sm text-[#C5C6C7]">
          &gt; no packages match your filter
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(([slug, svc]) => (
            <ServiceCard
              key={slug}
              slug={slug}
              svc={svc}
              onStartInstantly={onStartInstantly}
              onBookCall={onBookCall}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ServiceCard({ slug, svc, onStartInstantly, onBookCall }) {
  return (
    <div
      data-testid={`catalog-card-${slug}`}
      className="group relative bg-[#0d1117] border border-[#1F2833] p-6 flex flex-col hover:border-[#FF3355]/60 transition-all hover:scale-[1.015]"
    >
      {svc.popular && (
        <div className="absolute -top-2.5 left-4 inline-flex items-center gap-1 bg-emerald-400 text-[#0B0C10] px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.22em] font-bold">
          <Star size={9} fill="currentColor" /> Popular
        </div>
      )}

      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B91C2C] mb-2">
        {svc.categoryLabel}
      </div>
      <h3 className="font-display text-xl font-bold tracking-tight text-[#F5F5F7] group-hover:text-[#FF3355] transition-colors">
        {svc.name}
      </h3>
      {svc.tagline && (
        <p className="mt-2 text-sm text-[#C5C6C7] leading-relaxed line-clamp-2">
          {svc.tagline}
        </p>
      )}

      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-display text-2xl font-bold text-[#FF3355]">
          {svc.priceLabel}
        </span>
      </div>

      <ul className="mt-4 space-y-2 flex-1">
        {(svc.deliverables || []).slice(0, 3).map((d, i) => (
          <li
            key={i}
            className="te-lime-accent flex items-start gap-2 text-xs text-[#C5C6C7]"
          >
            <span className="text-emerald-400 mt-0.5">→</span>
            <span className="line-clamp-2">{d}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6 flex gap-2">
        <button
          onClick={() => onStartInstantly(slug, svc)}
          data-testid={`catalog-start-${slug}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#B91C2C] text-[#0B0C10] px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] transition-colors"
        >
          <Zap size={12} /> Start Instantly
        </button>
        <button
          onClick={() => onBookCall(slug, svc)}
          data-testid={`catalog-book-${slug}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 border border-emerald-400/60 text-emerald-400 hover:bg-emerald-400/10 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors"
        >
          <PhoneCall size={12} /> Strategy Call
        </button>
      </div>

      <a
        href={`/services/${slug}`}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]/70 hover:text-[#FF3355] transition-colors"
      >
        Full details <ArrowUpRight size={10} />
      </a>
    </div>
  );
}
